import { BugDefinition } from '../types/autofix';
import { targetAppStore } from './store';

export const REGISTERED_BUGS: Record<string, BugDefinition> = {
  'bug-1': {
    id: 'bug-1',
    name: 'The Phantom NaN (Zero-Divide in Discount Calculation)',
    description:
      'Applying a percentage coupon to a shopping cart containing a $0 promotional item (DevPack Stickers) causes division by zero. Total collapses to NaN and checkout payment gateway rejects transaction.',
    severity: 'critical',
    category: 'Arithmetic & Zero-Division Propagation',
    affectedModule: 'src/target-app/components/CheckoutForm.tsx',
    trigger: () => {
      targetAppStore.setBugState('bug-1', true);
      targetAppStore.applyDiscount('SUMMER30');
    },
    isActive: () => targetAppStore.getState().isBug1Active,
    reset: () => {
      targetAppStore.setBugState('bug-1', false);
    },
    rootCause:
      'Formula evaluates `couponPct / item.price` when item.price === 0. In JavaScript, 30 / 0 yields `Infinity`, and 0 * Infinity produces `NaN`. This propagates up the arithmetic tree until the final total is `NaN`.',
    fixExplanation:
      'Safely guard against zero-price promotional items. Use standard percentage arithmetic `Math.min(item.price, (item.price * couponPct) / 100)` and clamp total with `Number.isFinite(total) ? Math.max(0, total) : 0`.',
    buggyCode: `// ❌ BUGGY: Zero-price promo item causes 0 * (couponPct / item.price) -> 0 * Infinity -> NaN
const calculateItemDiscount = (item: CartItem, couponRate: number) => {
  const multiplier = item.price === 0 ? (couponRate / item.price) : (couponRate / 100);
  return item.price * multiplier; // Evaluates to NaN on $0 items!
};

export const computeTotal = (items: CartItem[], coupon: number) => {
  const subtotal = items.reduce((acc, i) => acc + i.product.price * i.quantity, 0);
  const discounts = items.reduce((acc, i) => acc + calculateItemDiscount(i.product, coupon), 0);
  return subtotal - discounts; // Returns NaN!
};`,
    fixedCode: `// ✅ AUTOPATCHED: Safe discount evaluation & finite arithmetic boundary
const calculateItemDiscount = (item: CartItem, couponRate: number) => {
  if (item.price <= 0) return 0; // Free promo items receive 0 discount
  const safeRate = Math.min(100, Math.max(0, couponRate)) / 100;
  return item.price * safeRate;
};

export const computeTotal = (items: CartItem[], coupon: number) => {
  const subtotal = items.reduce((acc, i) => acc + i.product.price * i.quantity, 0);
  const discounts = items.reduce((acc, i) => acc + calculateItemDiscount(i.product, coupon), 0);
  const rawTotal = subtotal - discounts;
  return Number.isFinite(rawTotal) ? Math.max(0, Math.round(rawTotal * 100) / 100) : 0;
};`,
  },

  'bug-2': {
    id: 'bug-2',
    name: 'Negative Price Exploit (Unclamped Coupon Stacking)',
    description:
      'Customers can stack multiple promotional vouchers (e.g. WELCOME20 + FLASH50 + VIP40 = 110% discount). Without an upper bound clamp, the net price becomes negative, causing the store to owe money to the buyer.',
    severity: 'high',
    category: 'Business Logic & Boundary Validation',
    affectedModule: 'src/target-app/components/DiscountEngine.tsx',
    trigger: () => {
      targetAppStore.setBugState('bug-2', true);
      targetAppStore.applyDiscount('WELCOME20');
      targetAppStore.applyDiscount('FLASH50');
      targetAppStore.applyDiscount('VIP40');
    },
    isActive: () => targetAppStore.getState().isBug2Active,
    reset: () => {
      targetAppStore.setBugState('bug-2', false);
    },
    rootCause:
      'Multiple promotional vouchers are summed without ceiling constraints. Cumulative discount rate reaches 110%, calculating `subtotal * (1 - 1.10)` which yields a negative payment amount.',
    fixExplanation:
      'Clamp stacked promotional discounts to a strict maximum business ceiling (e.g. 70% max discount). Disallow negative totals using `Math.max(0, ...)`.',
    buggyCode: `// ❌ BUGGY: Stacking coupons sum without upper limit
export const applyStackedPromos = (subtotal: number, promoCodes: string[]) => {
  const RATES: Record<string, number> = { WELCOME20: 20, FLASH50: 50, VIP40: 40 };
  const totalPercentage = promoCodes.reduce((sum, c) => sum + (RATES[c] || 0), 0);
  // If codes sum to 110%, 1 - 1.10 = -0.10!
  return subtotal * (1 - totalPercentage / 100);
};`,
    fixedCode: `// ✅ AUTOPATCHED: Clamp cumulative discount to business rules (max 70% off)
export const applyStackedPromos = (subtotal: number, promoCodes: string[]) => {
  const RATES: Record<string, number> = { WELCOME20: 20, FLASH50: 50, VIP40: 40 };
  const rawSum = promoCodes.reduce((sum, c) => sum + (RATES[c] || 0), 0);
  const clampedDiscount = Math.min(70, Math.max(0, rawSum)); // Max 70% off allowed
  const discounted = subtotal * (1 - clampedDiscount / 100);
  return Math.max(0, Math.round(discounted * 100) / 100);
};`,
  },

  'bug-3': {
    id: 'bug-3',
    name: 'Uncaught TypeError: Cannot read properties of null (reading "map")',
    description:
      'Navigating to past orders when order history is null (due to cold session or slow API sync) causes an unhandled React component crash that white-screens the browser window.',
    severity: 'critical',
    category: 'Null Reference & Render Crash',
    affectedModule: 'src/target-app/components/OrderHistory.tsx',
    trigger: () => {
      targetAppStore.setBugState('bug-3', true);
    },
    isActive: () => targetAppStore.getState().isBug3Active,
    reset: () => {
      targetAppStore.setBugState('bug-3', false);
    },
    rootCause:
      'Component assumes `orders` is always a populated Array and immediately calls `orders.map()`. When null is received, JavaScript throws an unhandled TypeError inside the render lifecycle.',
    fixExplanation:
      'Add defensive type checking using `Array.isArray(orders)` and provide an empty state fallback rendering with zero crash vulnerability.',
    buggyCode: `// ❌ BUGGY: Directly calling .map on potentially null orders state
export const OrderHistory = ({ orders }: { orders: Order[] | null }) => {
  return (
    <div className="orders-list">
      {orders.map((order) => ( // 💥 THROWS TypeError if orders is null!
        <div key={order.id} className="order-row">{order.id}</div>
      ))}
    </div>
  );
};`,
    fixedCode: `// ✅ AUTOPATCHED: Defensive null check and empty state fallback
export const OrderHistory = ({ orders }: { orders: Order[] | null }) => {
  const safeOrders = Array.isArray(orders) ? orders : [];
  
  if (safeOrders.length === 0) {
    return (
      <div className="empty-orders-state">
        <p className="text-2">No previous order history found for this account.</p>
      </div>
    );
  }

  return (
    <div className="orders-list">
      {safeOrders.map((order) => (
        <div key={order.id} className="order-row">{order.id}</div>
      ))}
    </div>
  );
};`,
  },

  'bug-4': {
    id: 'bug-4',
    name: 'Async State Desynchronization (Checkout Race Condition)',
    description:
      'Rapid concurrent coupon validation triggers un-synchronized state updates, causing the checkout total to calculate using stale price snapshots from a previous session.',
    severity: 'high',
    category: 'Concurrency & Asynchronous State Stale Closures',
    affectedModule: 'src/target-app/components/AsyncCheckoutPipeline.ts',
    trigger: () => {
      targetAppStore.setBugState('bug-4' as any, true);
    },
    isActive: () => (targetAppStore.getState() as any).isBug4Active || false,
    reset: () => {
      targetAppStore.setBugState('bug-4' as any, false);
    },
    rootCause:
      'Asynchronous validation promise chains resolve out-of-order without an AbortController or generation epoch sequence check. Stale response overwrites latest cart state.',
    fixExplanation:
      'Implement an AbortController signal or request ID epoch guard to discard stale out-of-order promise resolutions.',
    buggyCode: `// ❌ BUGGY: Async resolution without abort sequence or generation check
let lastResult: any = null;
export const validateVoucherAsync = async (code: string) => {
  const res = await api.validate(code);
  lastResult = res; // Out-of-order responses clobber current state!
  return lastResult;
};`,
    fixedCode: `// ✅ AUTOPATCHED: Epoch counter and AbortController request guard
let activeEpoch = 0;
export const validateVoucherAsync = async (code: string) => {
  const thisEpoch = ++activeEpoch;
  const res = await api.validate(code);
  if (thisEpoch !== activeEpoch) return null; // Discard stale closure
  return res;
};`,
  },

  'calc-div-zero': {
    id: 'calc-div-zero',
    name: 'ZeroDivisionError in ahfarmer/calculator (2.7k GitHub ⭐)',
    description:
      'In the real-world cloned React Calculator, evaluating 5 ÷ 0 triggers an uncaught ZeroDivisionError in operate.js, breaking the calculation display and locking the key panel.',
    severity: 'critical',
    category: 'Real-World Open Source Arithmetic Crash',
    affectedModule: 'real-world-app/src/logic/operate.js',
    trigger: () => {
      window.dispatchEvent(new CustomEvent('calc-trigger-fault'));
    },
    isActive: () => true,
    reset: () => {},
    rootCause:
      'Division operation (÷) evaluates two.eq(0) and throws an uncaught JavaScript Error, terminating the React setState lifecycle.',
    fixExplanation:
      'Hotpatch operate.js to safely clamp zero-division, returning "0" or "Cannot divide by 0" gracefully without crashing.',
    buggyCode: `// ❌ BUGGY in ahfarmer/calculator: Throws uncaught exception on 0 divisor
if (operation === "÷") {
  if (two.eq(0)) {
    throw new Error("ZeroDivisionError: Cannot divide by zero in operate.js");
  } else {
    return one.div(two).toString();
  }
}`,
    fixedCode: `// ✅ AUTOPATCHED: Safe arithmetic boundary preventing component crash
if (operation === "÷") {
  if (two.eq(0)) {
    return "0"; // Safely clamped to 0 without throwing uncaught exception
  } else {
    return one.div(two).toString();
  }
}`,
  },
};
